import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiPlus, FiEye, FiTrash2 } from 'react-icons/fi';
import api from '../../services/api';
import DataTable from '../../components/common/DataTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const AirExportList = () => {
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [etaFilter, setEtaFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [shipperFilter, setShipperFilter] = useState('');
  const [destFilter, setDestFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => { loadShipments(); }, [statusFilter, etaFilter, customerFilter, shipperFilter, destFilter, searchQuery]);

  const loadShipments = async () => {
    try {
      setLoading(true);
      const res = await api.get('/air-export');
      let data = res.data || [];
      if (statusFilter) data = data.filter(s => s.status === statusFilter);
      if (etaFilter) data = data.filter(s => s.eta && s.eta.startsWith(etaFilter));
      if (customerFilter) data = data.filter(s => (s.customer || '').toLowerCase().includes(customerFilter.toLowerCase()));
      if (shipperFilter) data = data.filter(s => (s.shipper || '').toLowerCase().includes(shipperFilter.toLowerCase()));
      if (destFilter) data = data.filter(s => (s.destination || '').toLowerCase().includes(destFilter.toLowerCase()));
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        data = data.filter(s =>
          (s.file_no || '').toLowerCase().includes(q) ||
          (s.mawb_no || '').toLowerCase().includes(q) ||
          (s.first_hawb_no || '').toLowerCase().includes(q) ||
          (s.carrier || '').toLowerCase().includes(q) ||
          (s.forwarding_agent || '').toLowerCase().includes(q) ||
          (s.customer || '').toLowerCase().includes(q) ||
          (s.shipper || '').toLowerCase().includes(q) ||
          (s.departure || '').toLowerCase().includes(q) ||
          (s.destination || '').toLowerCase().includes(q)
        );
      }
      setShipments(data);
    } catch (error) {
      toast.error('Failed to load shipments');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/air-export/${id}`);
      toast.success('Shipment deleted');
      setShipments(shipments.filter(s => s.id !== id));
      setConfirmDelete(null);
    } catch (error) {
      toast.error('Failed to delete shipment');
    }
  };

  const columns = [
    {
      key: 'file_no',
      label: 'File No.',
      sortable: true,
      render: (value, row) => (
        <Link
          to={`/air-export/${row.id}`}
          onClick={(e) => e.stopPropagation()}
          style={{ color: '#2563eb', textDecoration: 'none' }}
          onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
          onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
        >
          {value}
        </Link>
      ),
    },
    {
      key: 'mawb_no',
      label: 'MAWB No.',
      sortable: true,
      render: (value, row) => (
        <Link
          to={`/air-export/${row.id}`}
          onClick={(e) => e.stopPropagation()}
          style={{ color: '#2563eb', textDecoration: 'none' }}
          onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
          onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
        >
          {value}
        </Link>
      ),
    },
    { key: 'first_hawb_no', label: 'HAWB No.', sortable: true },
    { key: 'shipper', label: 'Shipper', sortable: true },
    { key: 'consignee', label: 'Consignee', sortable: true },
    { key: 'customer', label: 'Customer', sortable: true },
    { key: 'departure', label: 'Departure', sortable: true },
    { key: 'destination', label: 'Destination', sortable: true },
    { key: 'etd', label: 'ETD', sortable: true },
    { key: 'eta', label: 'ETA', sortable: true },
    { key: 'carrier', label: 'Carrier', sortable: true },
    { key: 'flight_no', label: 'Flight No.', sortable: true },
    { key: 'status', label: 'Status', sortable: true, type: 'status' },
  ];

  const renderActions = (row) => [
    <button key="view" className="btn btn-sm btn-primary" onClick={() => navigate(`/air-export/${row.id}`)} title="View">
      <FiEye /> VIEW
    </button>,
    <button key="delete" className="btn btn-sm btn-danger" onClick={() => setConfirmDelete(row)} title="Delete">
      <FiTrash2 />
    </button>,
  ];

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">AIR EXPORT - SHIPMENT LIST</h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-primary" onClick={() => navigate('/air-export/new')}>
              <FiPlus /> New Shipment
            </button>
          </div>
        </div>
        <div className="filter-bar" style={{ flexWrap: 'wrap' }}>
          <div className="filter-group">
            <label className="filter-label">Status:</label>
            <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All</option>
              <option value="Active">Active</option>
              <option value="In Transit">In Transit</option>
              <option value="Departed">Departed</option>
              <option value="Delivered">Delivered</option>
              <option value="Closed">Closed</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">ETA:</label>
            <input type="date" className="filter-input" style={{ width: '150px' }} value={etaFilter} onChange={(e) => setEtaFilter(e.target.value)} />
          </div>
          <div className="filter-group">
            <label className="filter-label">Customer:</label>
            <input type="text" className="filter-input" style={{ width: '140px' }} placeholder="All" value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value)} />
          </div>
          <div className="filter-group">
            <label className="filter-label">Shipper:</label>
            <input type="text" className="filter-input" style={{ width: '140px' }} placeholder="All" value={shipperFilter} onChange={(e) => setShipperFilter(e.target.value)} />
          </div>
          <div className="filter-group">
            <label className="filter-label">Destination:</label>
            <input type="text" className="filter-input" style={{ width: '140px' }} placeholder="All" value={destFilter} onChange={(e) => setDestFilter(e.target.value)} />
          </div>
          <div className="filter-group" style={{ flex: 1, marginLeft: 'auto' }}>
            <input type="text" className="filter-input" placeholder="Search by file no, MAWB, carrier, customer, shipper..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          </div>
        </div>
        <DataTable columns={columns} data={shipments} loading={loading} renderActions={renderActions} pageSize={15} />
      </div>

      {/* Delete Confirmation */}
      <ConfirmDialog isOpen={!!confirmDelete} title="Delete Shipment" message={`Are you sure you want to delete shipment ${confirmDelete?.file_no}?`} isDangerous onConfirm={() => handleDelete(confirmDelete.id)} onCancel={() => setConfirmDelete(null)} />
    </div>
  );
};

export default AirExportList;
